import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Upload, FileUp, Download, Loader2 } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReusableTable from "../components/Table/ReusableTable";
import CustomDialog from "../components/dialog/CustomDialog";
import { get, post } from "../utils/api";

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const truncated = Math.floor(Number(value) * 1000) / 1000;
  return `${truncated.toFixed(3)}%`;
};

const DistributorCommercialsMapping = () => {
  const [distributors, setDistributors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [selectedFund, setSelectedFund] = useState("");

  const [loadingDistributors, setLoadingDistributors] = useState(true);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [results, setResults] = useState([]);

  const [uploadTarget, setUploadTarget] = useState(null); // { rowId, type: 'agreement' | 'support' }
  const [uploadingKey, setUploadingKey] = useState(""); // `${rowId}-${type}` currently uploading
  const [downloadingKey, setDownloadingKey] = useState(""); // `${rowId}-${type}` currently downloading

  const [viewRows, setViewRows] = useState([]);

  const agreementInputRef = useRef(null);
  const supportInputRef = useRef(null);

  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        setLoadingDistributors(true);
        const response = await get("/distributorcommercialmapping/fetchDistributors");
        if (response.data?.Success) {
          setDistributors((response.data.Data || []).map((d) => d.distributor_name));
        } else {
          toast.error(response.data?.Message || "Failed to load distributors");
        }
      } catch (error) {
        console.error("Fetch distributors error:", error);
        toast.error("Error fetching distributors");
      } finally {
        setLoadingDistributors(false);
      }
    };
    fetchDistributors();
  }, []);

  useEffect(() => {
    const fetchMappings = async () => {
      try {
        setLoadingMappings(true);
        const response = await post("/distributorcommercialmapping/search", {});
        if (response.data?.Success) {
          setResults(response.data.Data || []);
        } else {
          toast.error(response.data?.Message || "Failed to load commercial mappings");
        }
      } catch (error) {
        console.error("Fetch commercial mappings error:", error);
        toast.error("Error fetching commercial mappings");
      } finally {
        setLoadingMappings(false);
      }
    };

    fetchMappings();
  }, []);

  const handleDistributorChange = async (e) => {
    const distributorName = e.target.value;
    setSelectedDistributor(distributorName);
    setSelectedFund("");
    setFunds([]);

    if (!distributorName) return;

    try {
      setLoadingFunds(true);
      const response = await post("/distributorcommercialmapping/fetchFundsByDistributor", {
        distributor_name: distributorName,
      });
      if (response.data?.Success) {
        setFunds((response.data.Data || []).map((f) => f.fund_name));
      } else {
        toast.error(response.data?.Message || "Failed to load funds");
      }
    } catch (error) {
      console.error("Fetch funds error:", error);
      toast.error("Error fetching funds");
    } finally {
      setLoadingFunds(false);
    }
  };

  const groupedResults = useMemo(() => {
    const grouped = new Map();

    results
      .filter((record) =>
        (!selectedDistributor || record.distributor_name === selectedDistributor) &&
        (!selectedFund || record.fund_name === selectedFund)
      )
      .forEach((record) => {
        const groupKey = JSON.stringify([
          record.distributor_name || "",
          record.fund_name || "",
          record.date_of_execution || "",
        ]);
        const existingGroup = grouped.get(groupKey);

        if (existingGroup) {
          existingGroup.commercialRows.push(record);
        } else {
          grouped.set(groupKey, {
            ...record,
            commercialRows: [record],
          });
        }
      });

    return Array.from(grouped.values());
  }, [results, selectedDistributor, selectedFund]);

  const triggerUpload = (row, type) => {
    setUploadTarget({ rowId: row.id, type });
    if (type === "agreement") {
      agreementInputRef.current?.click();
    } else {
      supportInputRef.current?.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file || !uploadTarget) return;

    const { rowId, type } = uploadTarget;
    const key = `${rowId}-${type}`;
    const endpoint =
      type === "agreement"
        ? "/distributorcommercialmapping/uploadAgreement"
        : "/distributorcommercialmapping/uploadSupportDoc";

    try {
      setUploadingKey(key);
      const formData = new FormData();
      formData.append("id", rowId);
      formData.append("file", file);

      const response = await post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.Success) {
        toast.success(
          type === "agreement" ? "Agreement uploaded successfully" : "Supporting document uploaded successfully"
        );
        const pathField = type === "agreement" ? "uploaded_agreement_path" : "uploaded_supportdoc_path";
        setResults((prev) =>
          prev.map((row) =>
            row.id === rowId ? { ...row, [pathField]: response.data.Data?.[pathField] } : row
          )
        );
      } else {
        toast.error(response.data?.Message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading file");
    } finally {
      setUploadingKey("");
      setUploadTarget(null);
    }
  };

  const handleDownload = async (row, type) => {
    const pathField = type === "agreement" ? "uploaded_agreement_path" : "uploaded_supportdoc_path";
    const filePath = row[pathField];
    if (!filePath) return;

    const endpoint =
      type === "agreement"
        ? `/distributorcommercialmapping/downloadAgreement/${row.id}`
        : `/distributorcommercialmapping/downloadSupportDoc/${row.id}`;
    const key = `${row.id}-${type}`;

    try {
      setDownloadingKey(key);
      const response = await get(endpoint, { responseType: "blob" });

      // Server returns a JSON error body (status 200) when the file is missing.
      // Don't trust Content-Type alone: a cached response may still carry application/json.
      if (response.data?.type?.includes("application/json")) {
        let result = null;
        try {
          result = JSON.parse(await response.data.text());
        } catch {
          // Not JSON - it's the actual file.
        }
        if (result?.Success === false) {
          toast.error(result.Message || "File not available");
          return;
        }
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      // Stored value is a full S3/CloudFront URL (older rows: just the file name).
      link.setAttribute("download", filePath.split("/").pop());
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error(type === "agreement" ? "Error downloading agreement" : "Error downloading supporting document");
    } finally {
      setDownloadingKey("");
    }
  };

  const columns = [
    { Header: "Distributor Name", accessor: "distributor_name", size: 180 },
    { Header: "Fund Name", accessor: "fund_name", size: 150 },
    { Header: "Date of Execution", accessor: "date_of_execution", size: 150 },
    // { Header: "Class", accessor: "class_code", size: 90 },
    // { Header: "Size", accessor: "size", size: 150 },
    // {
    //   Header: "M.Fees",
    //   accessor: "m_fees_per",
    //   size: 100,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    // {
    //   Header: "Share In M.Fees",
    //   accessor: "share_in_m_fees_per",
    //   size: 140,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    // {
    //   Header: "Upfront",
    //   accessor: "upfront_fee_per",
    //   size: 100,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    // {
    //   Header: "Trail",
    //   accessor: "trail_fee_per",
    //   size: 100,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    // {
    //   Header: "More Than 15Cr",
    //   accessor: "fee_morethan_15cr_per",
    //   size: 130,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    // {
    //   Header: "Less Than 15Cr",
    //   accessor: "fee_lessthan_15cr_per",
    //   size: 130,
    //   Cell: ({ value }) => formatPercent(value),
    // },
    {
      Header: "ACTION",
      accessor: "action",
      size: 850,
      Cell: ({ row }) => {
        const record = row.original;
        const isUploadingAgreement = uploadingKey === `${record.id}-agreement`;
        const isUploadingSupport = uploadingKey === `${record.id}-support`;
        const isDownloadingAgreement = downloadingKey === `${record.id}-agreement`;
        const isDownloadingSupport = downloadingKey === `${record.id}-support`;

        return (
          <div className="flex flex-nowrap items-center gap-2 py-1 min-w-[820px] whitespace-nowrap">
            <button
              onClick={() => setViewRows(record.commercialRows)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] font-semibold text-indigo-700 shadow-sm transition-all hover:bg-indigo-100"
              title="View Commercial"
            >
              <Eye size={14} />
              View Commercial
            </button>

            <button
              onClick={() => triggerUpload(record, "agreement")}
              disabled={isUploadingAgreement}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700 shadow-sm transition-all hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              title="Upload Agreement"
            >
              {isUploadingAgreement ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload Agreement
            </button>

            <button
              onClick={() => triggerUpload(record, "support")}
              disabled={isUploadingSupport}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-700 shadow-sm transition-all hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              title="Upload Support"
            >
              {isUploadingSupport ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
              Upload Supporting Document
            </button>

            <button
              onClick={() => handleDownload(record, "agreement")}
              disabled={!record.uploaded_agreement_path || isDownloadingAgreement}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              title={record.uploaded_agreement_path ? "Download Agreement" : "No agreement uploaded yet"}
            >
              {isDownloadingAgreement ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download Agreement
            </button>

            <button
              onClick={() => handleDownload(record, "support")}
              disabled={!record.uploaded_supportdoc_path || isDownloadingSupport}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              title={record.uploaded_supportdoc_path ? "Download Support" : "No supporting document uploaded yet"}
            >
              {isDownloadingSupport ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download Supporting Document
            </button>
          </div>
        );
      },
    },
  ];

  const commercialColumns = [
    { Header: "Fund Name", accessor: "fund_name" },
    { Header: "Class", accessor: "class_code" },
    { Header: "Size", accessor: "size" },
    {
      Header: "M. Fees",
      accessor: "m_fees_per",
      Cell: ({ value }) => formatPercent(value),
    },
    {
      Header: "Share in M. Fees",
      accessor: "share_in_m_fees_per",
      Cell: ({ value }) => formatPercent(value),
    },
    {
      Header: "Upfront",
      accessor: "upfront_fee_per",
      Cell: ({ value }) => formatPercent(value),
    },
    {
      Header: "Trail",
      accessor: "trail_fee_per",
      Cell: ({ value }) => formatPercent(value),
    },
    {
      Header: "More Than 15 Cr",
      accessor: "fee_morethan_15cr_per",
      Cell: ({ value, row }) =>
        String(row.original.size || "").trim().toLowerCase() === "less than 15 cr"
          ? "-"
          : formatPercent(value),
    },
    {
      Header: "Less Than 15 Cr",
      accessor: "fee_lessthan_15cr_per",
      Cell: ({ value, row }) =>
        String(row.original.size || "").trim().toLowerCase() === "more than 15 cr"
          ? "-"
          : formatPercent(value),
    },
  ];

  return (
    <div className="w-full max-w-none p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      <input
        type="file"
        ref={agreementInputRef}
        onChange={handleFileSelected}
        className="hidden"
      />
      <input
        type="file"
        ref={supportInputRef}
        onChange={handleFileSelected}
        className="hidden"
      />

      <div className="bg-white p-4 rounded shadow space-y-4">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight border-b pb-2">
          Distributor Management
        </h1>

        <div className="flex flex-col md:flex-row gap-4 md:items-end">
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Distributor Name
            </label>
            <select
              value={selectedDistributor}
              onChange={handleDistributorChange}
              disabled={loadingDistributors}
              className="border border-gray-200 rounded-lg px-4 py-2 w-full h-[42px] text-sm font-bold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
            >
              <option value="">
                {loadingDistributors ? "Loading distributors..." : "All Distributors"}
              </option>
              {distributors.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Fund Name
            </label>
            <select
              value={selectedFund}
              onChange={(e) => setSelectedFund(e.target.value)}
              disabled={!selectedDistributor || loadingFunds}
              className="border border-gray-200 rounded-lg px-4 py-2 w-full h-[42px] text-sm font-bold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {loadingFunds ? "Loading funds..." : "All Funds"}
              </option>
              {funds.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

        </div>

        {loadingMappings ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-400">
            <Loader2 size={28} className="animate-spin text-indigo-500" />
            <span className="text-sm">Loading commercial mappings...</span>
          </div>
        ) : groupedResults.length > 0 ? (
          <ReusableTable columns={columns} data={groupedResults} />
        ) : (
          <p className="text-center text-gray-500 italic py-6">No commercial mapping records found.</p>
        )}
      </div>

      <CustomDialog
        visible={viewRows.length > 0}
        onHide={() => setViewRows([])}
        header={`Commercial Details${viewRows[0]?.distributor_name ? ` - ${viewRows[0].distributor_name}` : ""}`}
        width="calc(100vw - 2rem)"
        style={{ maxWidth: "none" }}
      >
        {viewRows.length > 0 && (
          <div className="overflow-x-auto">
            <ReusableTable columns={commercialColumns} data={viewRows} />
          </div>
        )}
      </CustomDialog>
    </div>
  );
};

export default DistributorCommercialsMapping;
